import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import {
  LearnerObjectiveAccessCompactModel,
  LearnerObjectiveAccessFullModel,
  ObjectiveWorkflowState,
} from '@/api/models/aggregate'
import { CourseDateWithIcon } from '@/components/courses/CourseDateWithIcon/CourseDateWithIcon'
import { ProgressBar } from '@/components/ProgressBar/ProgressBar'
import { usePrepareCourseInfo } from '@/hooks/usePrepareCourseInfo'
import { clamp } from '@/utils'
import styles from './CourseDetailsComponent.module.scss'

interface Props {
  aggregate: LearnerObjectiveAccessFullModel
}

export const CourseBottom: React.FC<Props> = ({ aggregate }) => {
  const { t } = useTranslation()

  const { startedDate, isNotStarted, finishedDate, isFinished, submittedDate, dueDate } = usePrepareCourseInfo(
    aggregate as LearnerObjectiveAccessCompactModel,
  )

  const isInProgress =
    !!aggregate.objectiveWorkflowAggregate &&
    aggregate.objectiveWorkflowAggregate.lastObjectiveWorkflow?.state &&
    aggregate.objectiveWorkflowAggregate.lastObjectiveWorkflow?.state !== ObjectiveWorkflowState.finished

  const isInGrading =
    !!aggregate.objectiveWorkflowAggregate?.lastObjectiveWorkflow &&
    [ObjectiveWorkflowState.inGrading, ObjectiveWorkflowState.graded, ObjectiveWorkflowState.gradingApproved].includes(
      aggregate.objectiveWorkflowAggregate.lastObjectiveWorkflow.state,
    )

  const progressValue = clamp(aggregate.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.progress!, 0, 1) * 100

  const StartInfo = () => (
    <div className={styles.resultsInfo}>
      <CourseDateWithIcon text={t('courseStates.started')} value={startedDate!} />
    </div>
  )

  const SubmittedInfo = () => (
    <>
      <div className={styles.resultsInfo}>
        {t('courseStates.submitted')}: {submittedDate!}
      </div>
      <ProgressBar value={progressValue} />
    </>
  )

  const FinishInfo = () => (
    <div className={styles.resultsInfo}>
      <CourseDateWithIcon text={t('courseStates.completed')} value={finishedDate!} />
    </div>
  )

  if (isNotStarted) {
    return null
  }

  if (isInGrading) {
    return (
      <div className={cn(styles.cardBottom, styles.withProgressBar)}>
        <SubmittedInfo />
      </div>
    )
  }

  if (isInProgress) {
    return (
      <div className={cn(styles.cardBottom, styles.withProgressBar)}>
        <StartInfo />
        <ProgressBar value={progressValue} />
      </div>
    )
  }

  if (isFinished) {
    return (
      <div className={styles.cardBottom}>
        <FinishInfo />
      </div>
    )
  }

  if (dueDate) {
    return (
      <div className={styles.cardBottom}>
        <div className={styles.resultsInfo}>
          <CourseDateWithIcon text={t('courseDetails.dueDate')} icon="CalendarMediumStroke" value={dueDate} />
        </div>
      </div>
    )
  }

  return null
}
